/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import { graphql, useStaticQuery } from 'gatsby';
import marked from 'marked';
import React, { PropsWithChildren, useMemo } from 'react';
import { SITE_URL } from '../theme/constants';
import { ArticleStructuredDataType } from '../utils/blogTypes';
import { IQuestion } from './Questions/Question';

export const organization = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Stormotion',
  description:
    'Stormotion is an agile Digital Studio, that helps tech-star Startups and SME businesses from Western Europe, USA and Australia to Develop cutting-edge Mobile and Web Apps. Our domains include exciting areas like Fitness, Mental Health, Well-being, and IoT.',
  url: SITE_URL,
  sameAs: [
    'https://www.facebook.com/stormotion/',
    'https://www.instagram.com/stormotion_team/',
    'https://www.linkedin.com/company/stormotion/',
  ],
};

interface Props {
  description?: string;
  lang?: string;
  questions?: IQuestion[];
  articleStructuredData?: ArticleStructuredDataType;
  title?: string;
}

const SEO: React.FC<PropsWithChildren<Props>> = ({
  description,
  lang,
  title,
  questions,
  children,
  articleStructuredData,
}) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          version
        }
      }
    }
  `);

  const metaDescription = description || site.siteMetadata.description;

  const version = site.siteMetadata.version;

  const authorUri =
    'https://stormotion.io/blog/authors/' +
    articleStructuredData?.authorName.replace(/\s+/g, '-').toLowerCase();

  const defaultMeta = useMemo(() => {
    return (
      <>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta name="version" content={version} />
      </>
    );
  }, [metaDescription, title, version]);

  const metaWithRobots = useMemo(
    () =>
      process.env.GATSBY_ENVIRONMENT === 'PROD' ? (
        defaultMeta
      ) : (
        <>
          {defaultMeta}
          <meta name="robots" content="noindex,nofollow" />
        </>
      ),
    [defaultMeta]
  );

  const ArticleStructuredData = useMemo(
    () =>
      articleStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Article',
                headline: articleStructuredData.title,
                author: {
                  '@type': 'Person',
                  name: articleStructuredData.authorName,
                  url: authorUri,
                },
                image: [articleStructuredData.imageUrl],
                mainEntityOfPage: {
                  '@type': 'WebPage',
                  '@id': `${SITE_URL}blog/${articleStructuredData.slug}/`,
                },
                datePublished: articleStructuredData.dateModified
                  ? articleStructuredData.dateModified
                  : articleStructuredData.datePublished,
              },
              ...(articleStructuredData?.faqs.length > 0
                ? [
                    {
                      '@type': 'FAQPage',
                      mainEntity: articleStructuredData?.faqs?.map?.((faq) => ({
                        '@type': 'Question',
                        name: faq.question,
                        acceptedAnswer: {
                          '@type': 'Answer',
                          text: faq.answer,
                        },
                      })),
                    },
                  ]
                : []),
            ],
          })}
        </script>
      ),
    [articleStructuredData]
  );

  const QuestionsStructuredData = useMemo(
    () =>
      questions && (
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": ${JSON.stringify(
        questions?.map((question) => ({
          '@type': 'Question',
          name: question.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: marked(question.answer),
          },
        }))
      )}
    }
  `}
        </script>
      ),
    [questions]
  );

  return (
    <>
      <title>{title || site?.siteMetadata?.title}</title>
      {metaWithRobots}
      <script type="application/ld+json">{JSON.stringify(organization)}</script>
      {QuestionsStructuredData}
      {ArticleStructuredData}
      {children}
    </>
  );
};

SEO.defaultProps = {
  lang: `en`,
  description: ``,
};

export default SEO;
